import { ChangeDetectionStrategy, Component, computed, inject, viewChild } from "@angular/core";
import { Router } from "@angular/router";

import { HasRoleDirective } from "@core/directives/authorization/has-role.directive";
import { AuthService } from "@core/services/auth.service";
import { ThemeService } from "@core/services/theme.service";
import { DocumentAiHttpService } from "@documentation/services/document-ai-http.service";
import { DocumentsStore } from "@documentation/state/documents.store";
import { LoginRoutesConstants } from "@login/routes/login.routes.constants";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { ModalService } from "@shared/components/modal/modal.service";
import { ToggleComponent } from "@shared/components/toggle/toggle.component";
import { UserAvatarComponent } from "@shared/components/user-avatar/user-avatar.component";
import { PopoverConfigDirective } from "@shared/directives/popover-config.directive";
import { UserRole } from "@shared/models/user.model";

@Component({
  selector: "component-user-profile-popup",
  imports: [NgbPopover, CustomIconComponent, ToggleComponent, PopoverConfigDirective, UserAvatarComponent, HasRoleDirective],
  templateUrl: "./user-profile-popup.component.html",
  styleUrl: "./user-profile-popup.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfilePopupComponent {
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _store = inject(DocumentsStore);
  private readonly _modalService = inject(ModalService);
  private readonly _documentAiService = inject(DocumentAiHttpService);

  protected readonly popover = viewChild(NgbPopover);
  protected readonly user = computed(() => this._authService.currentUser());
  protected readonly UserRole = UserRole;
  protected readonly themeService = inject(ThemeService);
  protected readonly userAvatarLetter = computed(() =>
    (this.user()?.username || this.user()?.email || " ")!.charAt(0).toUpperCase()
  );

  protected readonly planName = computed(() => {
    return this.user()?.organization?.plan?.name || "No plan";
  });

  protected readonly displayName = computed(() => {
    return this.user()?.displayName;
  });

  protected readonly email = computed(() => {
    return this.user()?.email || "";
  });

  // Only keep the custom modifier configuration
  protected readonly popoverConfig = {
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [2, 16]
        }
      }
    ]
  };

  protected toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  protected onGiveFeedback(): void {
    window.open("https://docs.google.com/forms/d/e/1FAIpQLSeV8ID9p6pzoDiFSqjAoEmdHY5jxFEgxm0KyxD7v2lxIaxNOA/viewform", "_blank");
    this.closePopover();
  }

  protected onPricing(): void {
    this._modalService.openBillingModal(this.user()?.hasPlan ?? false);
    this.closePopover();
  }

  protected onViewProfile(): void {
    const user = this.user();
    if (user) {
      this._modalService.openUserProfileModal(user);
      this.closePopover();
    }
  }

  private closePopover(): void {
    this.popover()?.close();
  }

  protected async onLogout(): Promise<void> {
    if (this._store.isDirty()) {
      const proceed = await this._store.handleUnsavedChangesBeforeNavigation();
      if (!proceed) return;
    }
    const isTemp = this._authService.currentUser()?.isTemporary;
    if (isTemp) {
      const choice = await this._modalService.confirmTempLogout();
      if (choice === "upgrade") {
        this._modalService.openUpgradeModel(this.user()!);
        return;
      }
      if (choice !== "logout") return;
    }

    this._store.setNavigatingSafely(true);

    // Disconnect WebSocket before logout
    this._documentAiService.disconnect();

    this._authService.logout().subscribe(() => {
      this._router.navigate([LoginRoutesConstants.main()]);
    });
  }
}
